import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Alert, LinearProgress,
  Stack, Chip, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider,
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  Edit as EditIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { platformApi } from '../api/services';

interface EmailSettings {
  provider: string;
  fromEmail: string;
  fromName: string;
  isConfigured: boolean;
  apiKey?: string;
  domain?: string;
  smtpHost?: string;
  smtpPort?: number;
  smtpUsername?: string;
}

interface EmailLog {
  id: string;
  toEmail: string;
  subject: string;
  status: string;
  sentAt: string;
  errorMessage?: string;
}

const COLORS = {
  primary: '#6C9BFF',
  secondary: '#FF8A65',
  success: '#69F0AE',
  error: '#FF5252',
  warning: '#FFB74D',
  bgPaper: '#121829',
  textPrimary: '#E8ECF4',
  textSecondary: '#8892A8',
  gridLine: '#1E293B',
};

export default function PlatformEmailPage() {
  const [settings, setSettings] = useState<EmailSettings | null>(null);
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Edit dialog
  const [showEdit, setShowEdit] = useState(false);
  const [editSettings, setEditSettings] = useState<Partial<EmailSettings>>({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Test email dialog
  const [showTest, setShowTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testLoading, setTestLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [settingsData, logsData] = await Promise.all([
        platformApi.getEmailSettings(),
        platformApi.getEmailLogs(50),
      ]);
      // Map backend field names to frontend field names
      setSettings({
        ...settingsData,
        domain: settingsData.mailgunDomain,
        apiKey: settingsData.mailgunApiKey || settingsData.sendGridApiKey,
      });
      setLogs(logsData);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load email settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleOpenEdit = () => {
    if (settings) {
      setEditSettings({ ...settings });
    } else {
      setEditSettings({ provider: 'Mailgun', fromEmail: '', fromName: '' });
    }
    setShowEdit(true);
    setSaveError(null);
  };

  const handleSaveSettings = async () => {
    setSaveLoading(true);
    setSaveError(null);
    try {
      // Map frontend field names to backend field names
      const payload: any = {
        provider: editSettings.provider,
        fromEmail: editSettings.fromEmail,
        fromName: editSettings.fromName,
        mailgunDomain: editSettings.domain,
        mailgunApiKey: editSettings.apiKey,
        mailgunRegion: 'EU',
        sendGridApiKey: editSettings.provider === 'SendGrid' ? editSettings.apiKey : undefined,
        smtpHost: editSettings.smtpHost,
        smtpPort: editSettings.smtpPort,
        smtpUsername: editSettings.smtpUsername,
      };
      await platformApi.updateEmailSettings(payload);
      setShowEdit(false);
      setSuccess('Email settings updated successfully');
      load();
    } catch (err: any) {
      setSaveError(err.response?.data?.message || err.message || 'Failed to save settings');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail.trim()) return;
    setTestLoading(true);
    setError(null);
    try {
      await platformApi.sendTestEmail(testEmail);
      setShowTest(false);
      setTestEmail('');
      setSuccess('Test email sent successfully');
      // Reload logs to show the test email
      const logsData = await platformApi.getEmailLogs(50);
      setLogs(logsData);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to send test email');
      setShowTest(false);
    } finally {
      setTestLoading(false);
    }
  };

  const updateEditField = (field: string, value: any) => {
    setEditSettings(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EmailIcon sx={{ color: 'error.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h4">Email Settings</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Configure email provider and view delivery logs
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<SendIcon />}
            onClick={() => setShowTest(true)}
            disabled={!settings || (!settings.isConfigured && !settings.provider)}
          >
            Send Test Email
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<EditIcon />}
            onClick={handleOpenEdit}
            sx={{ fontWeight: 600 }}
          >
            Edit Settings
          </Button>
        </Stack>
      </Box>

      {loading && <LinearProgress color="error" sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Stack spacing={2.5}>
        {/* Current Settings Card */}
        <Card sx={{ bgcolor: COLORS.bgPaper }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: COLORS.textPrimary, fontWeight: 600 }}>
              Current Configuration
            </Typography>
            {settings ? (
              <Stack spacing={2}>
                <Stack direction="row" spacing={4} flexWrap="wrap">
                  <Box>
                    <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>Provider</Typography>
                    <Typography variant="body1" sx={{ color: COLORS.textPrimary, fontWeight: 500 }}>
                      {settings.provider || 'Not configured'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>From Email</Typography>
                    <Typography variant="body1" sx={{ color: COLORS.textPrimary, fontWeight: 500 }}>
                      {settings.fromEmail || 'Not set'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>From Name</Typography>
                    <Typography variant="body1" sx={{ color: COLORS.textPrimary, fontWeight: 500 }}>
                      {settings.fromName || 'Not set'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        icon={settings.isConfigured ? <CheckIcon /> : <ErrorIcon />}
                        label={settings.isConfigured ? 'Configured' : 'Not Configured'}
                        size="small"
                        color={settings.isConfigured ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Stack>
                {settings.provider === 'Mailgun' && settings.domain && (
                  <Box>
                    <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>Mailgun Domain</Typography>
                    <Typography variant="body2" sx={{ color: COLORS.textPrimary, fontFamily: 'monospace' }}>
                      {settings.domain}
                    </Typography>
                  </Box>
                )}
                {settings.provider === 'SMTP' && settings.smtpHost && (
                  <Box>
                    <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>SMTP Host</Typography>
                    <Typography variant="body2" sx={{ color: COLORS.textPrimary, fontFamily: 'monospace' }}>
                      {settings.smtpHost}:{settings.smtpPort}
                    </Typography>
                  </Box>
                )}
              </Stack>
            ) : !loading ? (
              <Typography color="text.secondary">No email settings configured. Click "Edit Settings" to set up.</Typography>
            ) : null}
          </CardContent>
        </Card>

        {/* Email Logs */}
        <Card sx={{ bgcolor: COLORS.bgPaper }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: COLORS.textPrimary, fontWeight: 600 }}>
              Recent Email Logs
            </Typography>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Sent At</TableCell>
                    <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>To</TableCell>
                    <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Subject</TableCell>
                    <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map(log => (
                    <TableRow key={log.id} hover>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontFamily: 'monospace' }}>
                          {new Date(log.sentAt).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: COLORS.textPrimary }}>{log.toEmail}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: COLORS.textPrimary, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.subject}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.status}
                          size="small"
                          color={log.status === 'Sent' || log.status === 'Delivered' ? 'success' : log.status === 'Failed' ? 'error' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: COLORS.error, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.errorMessage || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && logs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary" sx={{ py: 4 }}>No email logs yet</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Stack>

      {/* Edit Settings Dialog */}
      <Dialog open={showEdit} onClose={() => setShowEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Email Settings</DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Provider"
              select
              fullWidth
              value={editSettings.provider || 'Mailgun'}
              onChange={e => updateEditField('provider', e.target.value)}
            >
              <MenuItem value="Mailgun">Mailgun</MenuItem>
              <MenuItem value="SendGrid">SendGrid</MenuItem>
              <MenuItem value="SMTP">SMTP</MenuItem>
            </TextField>

            <Stack direction="row" spacing={2}>
              <TextField
                label="From Email"
                type="email"
                fullWidth
                value={editSettings.fromEmail || ''}
                onChange={e => updateEditField('fromEmail', e.target.value)}
                placeholder="noreply@example.com"
              />
              <TextField
                label="From Name"
                fullWidth
                value={editSettings.fromName || ''}
                onChange={e => updateEditField('fromName', e.target.value)}
                placeholder="Cadence"
              />
            </Stack>

            <Divider />

            {(editSettings.provider === 'Mailgun' || editSettings.provider === 'SendGrid') && (
              <>
                <TextField
                  label="API Key"
                  fullWidth
                  type="password"
                  value={editSettings.apiKey || ''}
                  onChange={e => updateEditField('apiKey', e.target.value)}
                  placeholder="Enter API key"
                />
                {editSettings.provider === 'Mailgun' && (
                  <TextField
                    label="Domain"
                    fullWidth
                    value={editSettings.domain || ''}
                    onChange={e => updateEditField('domain', e.target.value)}
                    placeholder="mg.example.com"
                  />
                )}
              </>
            )}

            {editSettings.provider === 'SMTP' && (
              <>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="SMTP Host"
                    fullWidth
                    value={editSettings.smtpHost || ''}
                    onChange={e => updateEditField('smtpHost', e.target.value)}
                    placeholder="smtp.example.com"
                  />
                  <TextField
                    label="Port"
                    type="number"
                    fullWidth
                    value={editSettings.smtpPort || 587}
                    onChange={e => updateEditField('smtpPort', parseInt(e.target.value) || 587)}
                    inputProps={{ min: 1, max: 65535 }}
                  />
                </Stack>
                <TextField
                  label="SMTP Username"
                  fullWidth
                  value={editSettings.smtpUsername || ''}
                  onChange={e => updateEditField('smtpUsername', e.target.value)}
                />
                <TextField
                  label="SMTP Password"
                  type="password"
                  fullWidth
                  value={(editSettings as any).smtpPassword || ''}
                  onChange={e => updateEditField('smtpPassword', e.target.value)}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEdit(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleSaveSettings} disabled={saveLoading}>
            {saveLoading ? 'Saving...' : 'Save Settings'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={showTest} onClose={() => setShowTest(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <TextField
            label="Recipient Email"
            type="email"
            fullWidth
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            placeholder="test@example.com"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTest(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleSendTest} disabled={testLoading || !testEmail.trim()}>
            {testLoading ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
